import { Button, Card, Form, Icon, IconSvg, Size, Theme, Tray } from '@busybusy/webapp-react-ui';
import {
  BudgetsIconOnboarding,
  DailyReportsIconOnboarding,
  DocumentSharingIcon,
  EquipmentIcon,
  IntegrationsIcon,
  JobCostingIcon,
  PayrollIcon,
  PhotosIcon,
  SchedulingIcon,
  VerifyIcon,
} from 'assets/icons';
import classNames from 'classnames';
import { HeaderDialog } from 'components';
import DualPanel from 'components/foundation/panels/DualPanel/DualPanel';
import Typography from 'components/foundation/text/Typography/Typography';
import { useOpenable } from 'hooks';
import useBrandTitle from 'hooks/meta/useBrandTitle';
import { FunctionComponent, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { t } from 'utils/localize';
import './OwnerOnboardingBusinessNeedsPage.scss';

export interface IOwnerOnboardingBusinessNeedsPageProps {
  className?: ClassName;
  formData: IBusinessNeedsOnboardingData;
  onBusinessNeedsFormSubmit: (form?: IBusinessNeedsOnboardingData) => void;
}

export interface IBusinessNeedsOnboardingData {
  jobCosting: boolean;
  photoDocumentation: boolean;
  budgets: boolean;
  documentSharing: boolean;
  equipment: boolean;
  scheduling: boolean;
  fieldReports: boolean;
  buildTrustAndVerify: boolean;
  improvePayroll: boolean;
  softwareIntegrations: boolean;
}

export const BusinessNeedsTitleRecord: Record<keyof IBusinessNeedsOnboardingData, string> = {
  jobCosting: 'Job Costing',
  photoDocumentation: 'Photo Documentation',
  budgets: 'Budgets',
  documentSharing: 'Document Sharing',
  equipment: 'Equipment',
  scheduling: 'Scheduling',
  fieldReports: 'Field Reports',
  buildTrustAndVerify: 'Build Trust & Verify',
  improvePayroll: 'Improve Payroll',
  softwareIntegrations: 'Software Integrations',
};

export interface IOnboardingBusinessNeed {
  key: keyof IBusinessNeedsOnboardingData;
  title: string;
  body: string;
  icon: IconSvg;
}

const OwnerOnboardingBusinessNeedsPage: FunctionComponent<IOwnerOnboardingBusinessNeedsPageProps> = (props) => {
  const { formData, onBusinessNeedsFormSubmit, className } = props;

  const [localFormData, setLocalFormData] = useState<IBusinessNeedsOnboardingData>(formData);
  const noFeaturesSelectedOpenable = useOpenable();
  const brand = useBrandTitle();

  const classes = classNames('owner-onboarding-business-needs-page', className);

  const OnboardingBusinessNeeds: IOnboardingBusinessNeed[] = [
    {
      key: 'jobCosting',
      title: t('Job Costing'),
      body: t('Understand costs and improve profit with real-time job costing.'),
      icon: JobCostingIcon,
    },
    {
      key: 'photoDocumentation',
      title: t('Photo Documentation'),
      body: t('Cover your ass with job photos.'),
      icon: PhotosIcon,
    },
    {
      key: 'budgets',
      title: t('Budgets'),
      body: t('Know daily if you are ahead or behind on each job.'),
      icon: BudgetsIconOnboarding,
    },
    {
      key: 'documentSharing',
      title: t('Document Sharing'),
      body: t('Share SWPP, OSHA Forms, SDS, Cut Sheets & more to Projects.'),
      icon: DocumentSharingIcon,
    },
    {
      key: 'equipment',
      title: t('Equipment'),
      body: t('Machine Hours, Operator Hours, GPS Locations, Inspections, Load Tracking.'),
      icon: EquipmentIcon,
    },
    {
      key: 'scheduling',
      title: t('Scheduling'),
      body: t(
        'Clearly direct employees to their assigned location & duties. Precise resource allocation makes tracking time easy.'
      ),
      icon: SchedulingIcon,
    },
    {
      key: 'fieldReports',
      title: t('Field Reports'),
      body: t('Field Reports, Safety Meetings, Change Orders & more automatically from field to office.'),
      icon: DailyReportsIconOnboarding,
    },
    {
      key: 'buildTrustAndVerify',
      title: t('Build Trust & Verify'),
      body: t('Facial recognition and required on-site GPS ensure honesty.'),
      icon: VerifyIcon,
    },
    {
      key: 'improvePayroll',
      title: t('Improve Payroll'),
      body: t('Use your existing payroll company or use us. Either way payroll will be accurate, fast and easy.'),
      icon: PayrollIcon,
    },
    {
      key: 'softwareIntegrations',
      title: t('Software Integrations'),
      body: t('Easily get data in and out of {{brand}}.', { brand }),
      icon: IntegrationsIcon,
    },
  ];

  function handleCardClick(key: keyof IBusinessNeedsOnboardingData) {
    return () => setLocalFormData({ ...localFormData, [key]: !localFormData[key] });
  }

  function formRender(form: any) {
    function onFormSubmitMouseDown(e: any) {
      e.preventDefault();
    }

    return (
      <>
        <div className="card-container pb-11">
          {OnboardingBusinessNeeds.map((item) => {
            return (
              <Card
                key={item.key}
                type={'stroke'}
                onClick={handleCardClick(item.key)}
                className={'mb-4 ' + (localFormData[item.key] ? 'clicked' : undefined)}
              >
                <Tray spacing={3} className={'m-3'}>
                  <div className="icon-container">
                    <Icon svg={item.icon} theme={Theme.PRIMARY} size={Size.LARGE} />
                  </div>
                  <div>
                    <Typography tag="h2" fontSize="medium">
                      {item.title}
                    </Typography>
                    <Typography tag="h5" color="medium-gray">
                      {item.body}
                    </Typography>
                  </div>
                </Tray>
              </Card>
            );
          })}
        </div>
        <Button
          className={'form-button mb-4'}
          type={'primary'}
          onMouseDown={onFormSubmitMouseDown}
          onClick={form.handleSubmit}
        >
          {t('Next Step')}
        </Button>
      </>
    );
  }

  function renderForm() {
    return (
      <Form
        data={localFormData}
        onSubmit={
          !Object.values(localFormData).every((val) => val === false)
            ? onBusinessNeedsFormSubmit
            : noFeaturesSelectedOpenable.open
        }
        render={formRender}
        allowMultipleSubmissions={true}
      />
    );
  }

  function renderLeftContent() {
    return (
      <>
        <h2 className="py-2">{t('In addition to time tracking, what else can we help you with?')}</h2>
        <Typography tag="h5" color="medium-gray" className="py-2">
          {t('Select all that apply.')}
        </Typography>
      </>
    );
  }

  return (
    <>
      <DualPanel className={classes} leftContent={renderLeftContent} rightContent={renderForm} />
      <HeaderDialog
        title={t('No Features Selected')}
        isOpen={noFeaturesSelectedOpenable.isOpen}
        onClose={noFeaturesSelectedOpenable.close}
        divider={false}
      >
        <h4 className={'ml-6 mb-2'}>{t(`Are you sure you don't need help with anything else?`)}</h4>
        <Tray>
          <Button
            type={'primary'}
            className={'m-6'}
            onClick={() => {
              onBusinessNeedsFormSubmit();
            }}
          >
            {t('Continue')}
          </Button>
          <Button type={'secondary'} className={'my-6'} onClick={noFeaturesSelectedOpenable.close}>
            {t('Cancel')}
          </Button>
        </Tray>
      </HeaderDialog>
    </>
  );
};

export default OwnerOnboardingBusinessNeedsPage;
